import { constVoid, Lazy, pipe } from 'fp-ts/function';
import * as O from 'fp-ts/Option';
import * as T from 'fp-ts/Task';
import React, {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

import { DebouncedLineLoader } from '@layout/loaders/line-loader/LineLoader';
import { Authorizations, Profile } from '@modules/auth/model';

import * as AuthService from './service';

const AUTH_LOGOUT_KEY = 'PG_LOGOUT';

export interface AuthContextValue {
  profile: O.Option<Profile>;
  updateProfile: (profile: Profile) => void;
  requestUpdateProfile: Lazy<void>;
  defaultRoute: string;
  logout: T.Task<void>;
}

export const AuthContext = createContext<AuthContextValue>({
  profile: O.none,
  updateProfile: constVoid,
  requestUpdateProfile: constVoid,
  defaultRoute: '/',
  logout: T.never,
});

export const AuthContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(true);

  const [profile, setProfile] = useState<O.Option<Profile>>(O.none);

  const fetchProfile = useCallback(() => {
    pipe(
      AuthService.getProfile(),
      T.chainIOK(res => () => {
        setProfile(O.fromEither(res));
        setLoading(false);
      }),
    )();
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  useEffect(() => {
    const syncLogout = (event: StorageEvent) => {
      if (event.key === AUTH_LOGOUT_KEY) {
        setProfile(O.none);
      }
    };

    window.addEventListener('storage', syncLogout, { passive: true });

    return () => {
      window.removeEventListener('storage', syncLogout);
      localStorage.removeItem(AUTH_LOGOUT_KEY);
    };
  }, [navigate]);

  const updateProfile = useCallback((profile: Profile) => setProfile(O.some(profile)), []);

  const logout = useCallback(() => {
    return pipe(
      AuthService.logout(),
      T.chainIOK(() => () => {
        localStorage.setItem(AUTH_LOGOUT_KEY, Date.now().toString());
        navigate('/login', { replace: true });
        setProfile(O.none);
      }),
    )();
  }, [navigate]);
  const defaultRoute = useMemo(
    () =>
      pipe(
        profile,
        O.match(
          () => '/login', // Pas de profil
          userProfile => {
            return userProfile.isValidated ? '/doors' : '/profile';
          },
        ),
      ),
    [profile],
  );

  const ctx = useMemo<AuthContextValue>(
    () => ({
      profile,
      updateProfile,
      requestUpdateProfile: fetchProfile,
      defaultRoute,
      logout,
    }),
    [profile, updateProfile, fetchProfile, defaultRoute, logout],
  );

  return <AuthContext.Provider value={ctx}>{loading ? <DebouncedLineLoader /> : children}</AuthContext.Provider>;
};

export function useAuthContext(): AuthContextValue {
  return useContext(AuthContext);
}

export function useAuthorization(type: keyof Authorizations): boolean {
  const { profile } = useAuthContext();

  return pipe(
    profile,
    O.exists(profile => profile.authorizations[type]),
  );
}
